<template>
  <div :class="['file-table', { 'has-pager': showPaginator }]">
    <file-bulk-bar
      v-if="selectedItems.length"
      :count="selectedItems.length"
      :add-folders="bulkAddFolders"
      :remove-folders="bulkRemoveFolders"
      :can-download="bulkCan('canDownload')"
      :can-share="bulkCan('canShare')"
      :can-move="canMove"
      :can-delete="bulkCan('canDelete')"
      @download="$emit('bulk-download', selectedFiles())"
      @share="$emit('bulk-share', selectedFiles())"
      @delete="$emit('bulk-delete', selectedFiles())"
      @add-to-folder="bulkFolder($event, true)"
      @remove-from-folder="bulkFolder($event, false)"
      @clear="clearSelection"
    />
    <div
      v-if="showSelectAllBanner"
      class="select-all-banner"
      data-test="select-all-banner"
    >
      <template v-if="allMatchingSelected">
        All {{ totalFiles }} files are selected.
      </template>
      <template v-else>
        All {{ files.length }} files on this page are selected.
        <button
          v-if="totalFiles <= selectAllLimit"
          type="button"
          class="select-all-link"
          data-test="select-all-matching"
          :disabled="loadingAll"
          @click="selectAllMatching"
        >
          {{ loadingAll ? 'Selecting…' : `Select all ${totalFiles} files` }}
        </button>
        <span v-else>
          Narrow your search to select more than {{ selectAllLimit }} files.
        </span>
      </template>
      <span v-if="selectAllError" class="text-danger ml-2">
        {{ selectAllError }}
      </span>
    </div>
    <b-overlay :show="loading" rounded="sm">
      <template #overlay>
        <div class="text-center">
          <digger-loader />
        </div>
      </template>
      <b-table
        hover
        class="table"
        :items="files"
        :fields="computedFields"
        :sort-compare="sortCompare"
        :no-local-sorting="!isShareView"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        show-empty
        @sort-changed="onSortChanged"
        @row-clicked="openFile"
      >
        <template #head(select)>
          <b-form-checkbox
            :checked="allSelected"
            :indeterminate="someSelected"
            aria-label="Select all files on this page"
            data-test="select-all"
            @change="toggleAll"
          />
        </template>

        <template #cell(select)="{ item }">
          <div @click.stop>
            <b-form-checkbox
              :checked="isSelected(item)"
              :aria-label="'Select ' + item.display_name"
              data-test="select-row"
              @change="toggleOne(item)"
            />
          </div>
        </template>

        <template #empty>
          <div class="text-center text-muted py-4">{{ emptyText }}</div>
        </template>

        <template #cell(display_name)="{ item }">
          <div
            class="file-name"
            :draggable="canMove"
            @dragstart="onDragStart($event, item)"
          >
            <div class="file-icons">
              <div
                v-for="kind in attachmentKinds(item)"
                :key="kind"
                :class="['file-icon', `kind-${kind}`]"
              >
                {{ kindLabel(kind) }}
              </div>
            </div>
            <div class="file-name-text">
              <b>{{ item.display_name }}</b>
              <span v-if="item.is_new" class="file-new">New</span>
              <span
                v-if="item.expiry"
                :class="['file-expiry', `expiry-${item.expiry}`]"
                data-test="expiry-badge"
              >
                {{ expiryLabel(item.expiry) }}
              </span>
              <span
                v-for="label in visibleFolderLabels(item)"
                :key="label"
                class="file-folder"
              >
                <b-icon icon="folder-fill" font-scale="0.8" />
                {{ label }}
              </span>
              <span
                v-if="hiddenFolderLabels(item).length"
                v-b-tooltip.hover.top="hiddenFolderLabels(item).join(', ')"
                class="file-folder file-folder-more"
              >
                +{{ hiddenFolderLabels(item).length }}
              </span>
              <!-- The Owner and Added columns drop out on narrower screens; their
                   content moves to a line under the name instead. -->
              <div class="file-meta" data-test="file-meta">
                <span class="meta-kind d-sm-none">{{ kindsText(item) }}</span>
                <span
                  v-if="metaOwner(item)"
                  :class="['meta-owner', metaOwnerHide]"
                >
                  {{ metaOwner(item) }}
                </span>
                <span
                  :class="[
                    'meta-added',
                    'd-sm-none',
                    { 'with-owner': !!metaOwner(item) },
                  ]"
                >
                  {{ item.added }}
                </span>
              </div>
            </div>
          </div>
        </template>

        <template #cell(owner)="{ item }">
          {{ item.owner }}
          <div
            v-if="!isShareView && showsCreator(item)"
            class="owner-note"
            data-test="creator-note"
          >
            Created by {{ item.created_by }}
          </div>
        </template>

        <template #cell(actions)="{ item }">
          <b-dropdown
            v-if="hasMenu(item)"
            size="sm"
            variant="transparent"
            no-caret
            right
            boundary="window"
            aria-label="File options"
            @hidden="inFolders = false"
          >
            <template #button-content>
              <b-icon icon="three-dots" />
            </template>

            <template v-if="!inFolders">
              <b-dropdown-item-button
                v-if="canEdit(item)"
                @click="$emit('edit-file', item.file)"
              >
                <b-icon icon="pencil-square" font-scale="0.95" class="mr-3" />
                Edit
              </b-dropdown-item-button>
              <b-dropdown-item-button
                v-if="canDownload(item)"
                @click="$emit('download-file', item.file)"
              >
                <b-icon icon="download" font-scale="0.95" class="mr-3" />
                Download
              </b-dropdown-item-button>
              <b-dropdown-item-button
                v-if="canShare(item)"
                @click="$emit('share-file', item.file)"
              >
                <b-icon icon="cursor" font-scale="0.95" class="mr-3" />
                Share
              </b-dropdown-item-button>
              <template v-if="canMove">
                <b-dropdown-divider />
                <li role="presentation">
                  <button
                    type="button"
                    class="dropdown-item folders-item"
                    @click.stop="inFolders = true"
                  >
                    <span>
                      <b-icon icon="folder" font-scale="0.95" class="mr-3" />
                      Folders
                    </span>
                    <b-icon icon="chevron-right" font-scale="0.8" />
                  </button>
                </li>
              </template>
              <template v-if="canDelete(item)">
                <b-dropdown-divider />
                <b-dropdown-item-button
                  @click="$emit('delete-file', item.file)"
                >
                  <b-icon icon="trash" font-scale="0.95" class="mr-3" />
                  Delete
                </b-dropdown-item-button>
              </template>
            </template>

            <template v-else>
              <li role="presentation">
                <button
                  type="button"
                  class="dropdown-item folders-item"
                  @click.stop="inFolders = false"
                >
                  <span>
                    <b-icon icon="chevron-left" font-scale="0.8" class="mr-3" />
                    Back
                  </span>
                </button>
              </li>
              <b-dropdown-header>Add to folders</b-dropdown-header>
              <li
                v-for="folder in folders"
                :key="folder.id"
                role="presentation"
              >
                <button
                  type="button"
                  class="dropdown-item folders-item"
                  @click.stop="toggleFolder(item, folder.id)"
                >
                  <span>{{ folder.path || folder.label }}</span>
                  <b-icon
                    v-if="inFolder(item, folder.id)"
                    icon="check"
                    font-scale="1.2"
                  />
                </button>
              </li>
            </template>
          </b-dropdown>
        </template>
      </b-table>
    </b-overlay>

    <file-preview-drawer
      :item="previewItem"
      :folder-labels="previewItem ? folderNames(previewItem) : []"
      :owner-label="selectedFolder === 'received' ? 'From' : 'Owner'"
      :creator-label="selectedFolder === 'sent' ? 'Sent to' : 'Creator'"
      :can-download="!!previewItem && canDownload(previewItem)"
      :can-share="!!previewItem && canShare(previewItem)"
      :can-edit="!!previewItem && canEdit(previewItem)"
      :can-delete="!!previewItem && canDelete(previewItem)"
      @close="previewItem = null"
      @open-full="openFullFile"
      @download="$emit('download-file', $event)"
      @share="fromDrawer('share-file', $event)"
      @edit="fromDrawer('edit-file', $event)"
      @delete="fromDrawer('delete-file', $event)"
    />

    <b-pagination
      v-if="showPaginator"
      :value="currentPage"
      :total-rows="filesMeta.total"
      :per-page="filesMeta.per_page"
      align="center"
      class="pt-2"
      :limit="isPhone ? 3 : 5"
      :size="isPhone ? 'sm' : 'md'"
      :hide-goto-end-buttons="isPhone"
      @change="onPageChange"
    >
    </b-pagination>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import FileBulkBar from '@/components/files/FileBulkBar'
import viewport from '@/mixins/viewport'
import FilePreviewDrawer from '@/components/files/FilePreviewDrawer'
import { EXPIRY_LABELS } from '@/utils/fileExpiry'
import { FILE_DRAG_TYPE } from '@/utils/fileDrag'
import { fileKind } from '@/utils/fileTypeIcons'
import {
  canDeleteFile,
  canViewerEditFile,
  hasBusinessAdminFilePermission,
  isTradieOwnedFile,
} from '@/utils/filePermissions'

const MAX_FOLDER_CHIPS = 2
const KIND_LABELS = { pdf: 'PDF', doc: 'DOC', xls: 'XLS', img: 'IMG' }

export default {
  components: { FileBulkBar, FilePreviewDrawer },

  mixins: [viewport],

  props: {
    files: { type: Array, default: () => [] },
    folders: { type: Array, default: () => [] },
    // False when the user may not change folders (a role without files.edit).
    canOrganise: { type: Boolean, default: true },
    selectedFolder: { type: String, default: 'all' },
    loading: { type: Boolean, default: false },
    emptyMessage: { type: String, default: '' },
    filesMeta: { type: Object, default: () => ({}) },
    currentPage: { type: Number, default: 1 },
    // Changes when the list itself changes (folder, type, search, sort), not when paging.
    selectionKey: { type: String, default: '' },
    sortBy: { type: String, default: 'added' },
    sortDesc: { type: Boolean, default: true },
    // Loads every file in the current list (up to the limit) as table rows.
    loadAllRows: { type: Function, default: null },
    selectAllLimit: { type: Number, default: 300 },
  },

  data() {
    return {
      inFolders: false,
      // Rows ticked on any page, by id, so the selection survives paging.
      selection: {},
      lastSelectionKey: this.selectionKey,
      lastPage: this.currentPage,
      loadingAll: false,
      selectAllError: '',
      previewItem: null,
    }
  },

  computed: {
    ...mapGetters('auth', ['user', 'hasPermission']),

    isBusinessAdmin() {
      return !!this.user && this.user.user_type === 'business_admin'
    },

    isSupervisorTradie() {
      return !!(
        this.user &&
        this.user.user_type === 'tradie' &&
        this.user.is_supervisor
      )
    },

    canMove() {
      return this.canOrganise && this.folders.length > 0 && !this.isShareView
    },

    // The spinner replaces the rows only when there are none to show yet;
    // later reloads keep them on screen, dimmed.
    firstLoad() {
      return this.loading && this.files.length === 0
    },

    reloading() {
      return this.loading && this.files.length > 0
    },

    isShareView() {
      return ['received', 'sent'].includes(this.selectedFolder)
    },

    showPaginator() {
      return this.filesMeta.total > this.filesMeta.per_page
    },

    emptyText() {
      return this.emptyMessage || 'There are no files to show.'
    },

    selectedItems() {
      return Object.values(this.selection)
    },

    selectedIds() {
      return this.selectedItems.map((item) => item.id)
    },

    // Folders where at least one selected file isn't yet.
    bulkAddFolders() {
      return this.folders.filter(
        (folder) =>
          !this.selectedItems.every((item) => this.inFolder(item, folder.id)),
      )
    },

    // Folders that hold at least one selected file.
    bulkRemoveFolders() {
      return this.folders.filter((folder) =>
        this.selectedItems.some((item) => this.inFolder(item, folder.id)),
      )
    },

    allSelected() {
      return this.files.length > 0 && this.files.every(this.isSelected)
    },

    totalFiles() {
      return Number(this.filesMeta && this.filesMeta.total) || 0
    },

    allMatchingSelected() {
      return this.totalFiles > 0 && this.selectedItems.length >= this.totalFiles
    },

    showSelectAllBanner() {
      return (
        !!this.loadAllRows &&
        this.allSelected &&
        this.totalFiles > this.files.length
      )
    },

    someSelected() {
      return this.files.some(this.isSelected) && !this.allSelected
    },

    // Received and Sent carry a second column, so their columns wait for lg.
    ownerColumnClass() {
      return this.isShareView
        ? 'd-none d-lg-table-cell'
        : 'd-none d-md-table-cell'
    },

    metaOwnerHide() {
      return this.isShareView ? 'd-lg-none' : 'd-md-none'
    },

    computedFields() {
      const fields = [
        {
          key: 'select',
          label: '',
          thClass: 'select-cell',
          tdClass: 'select-cell',
        },
        {
          key: 'display_name',
          label: 'Name',
          sortable: true,
        },
        {
          key: 'owner',
          label: this.selectedFolder === 'received' ? 'From' : 'Owner',
          thClass: this.ownerColumnClass,
          tdClass: this.ownerColumnClass,
        },
        // Received and Sent need both (who it is from or went to); elsewhere the
        // creator is only worth a line when it is not the owner.
        ...(this.isShareView
          ? [
              {
                key: 'created_by',
                label: this.selectedFolder === 'sent' ? 'Sent to' : 'Creator',
                thClass: 'd-none d-lg-table-cell',
                tdClass: 'd-none d-lg-table-cell',
              },
            ]
          : []),
        {
          key: 'added',
          label: 'Added',
          sortable: true,
          thClass: 'd-none d-sm-table-cell',
          tdClass: 'd-none d-sm-table-cell',
        },
        {
          key: 'actions',
          label: '',
          tdClass: 'text-right',
        },
      ]

      return fields
    },
  },

  watch: {
    // Paging keeps the selection; any other new list (a filter, a reload after an action) clears it.
    files() {
      const paged =
        this.selectionKey === this.lastSelectionKey &&
        this.currentPage !== this.lastPage
      this.lastSelectionKey = this.selectionKey
      this.lastPage = this.currentPage
      if (!paged) this.selection = {}
    },
  },

  methods: {
    canDownload(item) {
      return hasBusinessAdminFilePermission(
        item.file,
        this.user,
        this.hasPermission,
        'download',
      )
    },

    // Same rules as the old Files page menu (FileOptions).
    canShare(item) {
      if (!this.isBusinessAdmin) return true
      if (this.selectedFolder === 'received') {
        return this.hasPermission('files.share')
      }
      if (isTradieOwnedFile(item.file)) return false
      return this.hasPermission('files.share')
    },

    // Edit and Delete follow the old Files page menu (FileOptions): never on
    // files shared with you, and supervisors only on their own files.
    canChange(item) {
      if (this.selectedFolder === 'received') return false
      const file = item.file
      const own = !!(file && file.user && file.user.mine)
      return !this.isSupervisorTradie || own
    },

    canEdit(item) {
      return (
        this.canChange(item) &&
        canViewerEditFile(item.file, this.user, this.hasPermission)
      )
    },

    canDelete(item) {
      const file = item.file
      return (
        this.canChange(item) &&
        !!file &&
        !file.deletion_scheduled_at &&
        !!file.deletable &&
        hasBusinessAdminFilePermission(
          file,
          this.user,
          this.hasPermission,
          'delete',
        ) &&
        canDeleteFile(file, this.user)
      )
    },

    hasMenu(item) {
      return (
        this.canEdit(item) ||
        this.canDownload(item) ||
        this.canShare(item) ||
        this.canDelete(item) ||
        this.canMove
      )
    },

    kindsText(item) {
      return this.attachmentKinds(item)
        .map((kind) => this.kindLabel(kind))
        .join(' / ')
    },

    kindLabel(kind) {
      return KIND_LABELS[kind] || 'FILE'
    },

    // One tile per distinct attachment type, first attachment's type first.
    attachmentKinds(item) {
      const attachments = (item.file && item.file.files) || []
      const kinds = attachments.map((attachment) => fileKind(attachment))
      return kinds.length ? [...new Set(kinds)] : [item.kind]
    },

    // "Added" shows text like "2 weeks ago", so sort by the real date instead.
    sortCompare(a, b, key) {
      if (key !== 'added') return null
      const time = (row) => (row.added_at ? row.added_at.getTime() : 0)
      return time(a) - time(b)
    },

    onPageChange(page) {
      this.$emit('page-change', page)
    },

    onSortChanged({ sortBy, sortDesc }) {
      this.$emit('sort-change', { sortBy, sortDesc })
    },

    expiryLabel(state) {
      return EXPIRY_LABELS[state]
    },

    isSelected(item) {
      return item.id in this.selection
    },

    toggleOne(item) {
      if (this.isSelected(item)) this.$delete(this.selection, item.id)
      else this.$set(this.selection, item.id, item)
    },

    // The header box ticks or unticks this page only; other pages keep theirs.
    toggleAll(checked) {
      this.files.forEach((item) => {
        if (checked) this.$set(this.selection, item.id, item)
        else this.$delete(this.selection, item.id)
      })
    },

    clearSelection() {
      this.selection = {}
    },

    async selectAllMatching() {
      if (this.loadingAll) return
      const key = this.selectionKey
      this.loadingAll = true
      this.selectAllError = ''
      try {
        const rows = await this.loadAllRows(this.selectAllLimit)
        if (key !== this.selectionKey) return
        const next = {}
        rows.forEach((row) => {
          next[row.id] = row
        })
        this.selection = next
      } catch (error) {
        this.selectAllError = 'Could not select all files. Please try again.'
      } finally {
        this.loadingAll = false
      }
    },

    selectedFiles() {
      return this.selectedItems.map((item) => item.file)
    },

    // A bulk action shows only when every selected file allows it.
    bulkCan(rule) {
      return (
        this.selectedItems.length > 0 &&
        this.selectedItems.every((item) => this[rule](item))
      )
    },

    bulkFolder(folderId, add) {
      this.$emit('bulk-folder', { files: this.selectedFiles(), folderId, add })
    },

    // Dragging a selected row carries the whole selection.
    onDragStart(event, item) {
      const ids = this.isSelected(item) ? this.selectedIds : [item.id]
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData(FILE_DRAG_TYPE, JSON.stringify(ids))
    },

    openFile(item) {
      if (item.is_new) this.$emit('open-file', item.id)
      this.previewItem = item
    },

    openFullFile(item) {
      this.$router.push(`/file/${item.id}`)
    },

    // These actions open their own dialogs or pages, so the drawer gets out of the way.
    fromDrawer(event, file) {
      this.previewItem = null
      this.$emit(event, file)
    },

    // Who it is from or went to, for the line under the name on narrow screens.
    metaOwner(item) {
      if (this.selectedFolder === 'received') {
        return item.owner ? 'From ' + item.owner : ''
      }
      if (this.selectedFolder === 'sent') {
        return item.created_by && item.created_by !== '-'
          ? 'Sent to ' + item.created_by
          : ''
      }
      return item.owner || ''
    },

    // The creator line is only shown when somebody other than the owner made the file.
    showsCreator(item) {
      return (
        !!item.created_by &&
        item.created_by !== '-' &&
        item.created_by !== item.owner
      )
    },

    inFolder(item, folderId) {
      return (item.folder_ids || []).map(String).includes(String(folderId))
    },

    // A file can be in any number of folders; this flips one of them.
    toggleFolder(item, folderId) {
      const id = String(folderId)
      const current = (item.folder_ids || []).map(String)
      const next = current.includes(id)
        ? current.filter((existing) => existing !== id)
        : current.concat(id)
      this.$emit('set-folders', { fileId: item.id, folderIds: next })
    },

    visibleFolderLabels(item) {
      return this.folderLabels(item).slice(0, MAX_FOLDER_CHIPS)
    },

    hiddenFolderLabels(item) {
      return this.folderLabels(item).slice(MAX_FOLDER_CHIPS)
    },

    folderNames(item) {
      return (item.folder_ids || [])
        .map(String)
        .map((id) => this.folders.find((folder) => folder.id === id))
        .filter(Boolean)
        .map((folder) => folder.path || folder.label)
    },

    // Names of the folders a file sits in, leaving out the folder already open.
    folderLabels(item) {
      return (item.folder_ids || [])
        .map(String)
        .filter((id) => id !== this.selectedFolder)
        .map((id) => this.folders.find((folder) => folder.id === id))
        .filter(Boolean)
        .map((folder) => folder.path || folder.label)
    },
  },
}
</script>

<style scoped>
.select-all-banner {
  padding: 6px 14px;
  background-color: #f3f6ff;
  border-bottom: 1px solid #e5e7eb;
  color: #374151;
  font-size: 13px;
}

.select-all-link {
  padding: 0;
  background: transparent;
  border: 0;
  color: #006cff;
  font-weight: 700;
}

.select-all-link:disabled {
  opacity: 0.6;
}

.file-table {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background-color: #ffffff;
}

.file-table /deep/ .table {
  margin-bottom: 0;
}

.file-table /deep/ thead th {
  padding: 10px 14px;
  border-top: 0;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
  color: #6b7280;
  font-size: 12px;
  font-weight: 700;
  vertical-align: middle;
}

.file-table /deep/ tbody td {
  padding: 8px 14px;
  border-top: 0;
  border-bottom: 1px solid #e5e7eb;
  background-color: #ffffff;
  color: #111827;
  font-size: 14px;
  vertical-align: middle;
}

.file-table /deep/ tbody tr:last-child td {
  border-bottom: 0;
}

/* No overflow:hidden on the card (it would clip the row menus), so round the corners here. */
.file-table /deep/ thead th:first-child {
  border-top-left-radius: 9px;
}

.file-table /deep/ thead th:last-child {
  border-top-right-radius: 9px;
}

.file-table:not(.has-pager) /deep/ tbody tr:last-child td:first-child {
  border-bottom-left-radius: 9px;
}

.file-table:not(.has-pager) /deep/ tbody tr:last-child td:last-child {
  border-bottom-right-radius: 9px;
}

.file-table /deep/ tbody tr {
  cursor: pointer;
}

.file-table /deep/ tbody tr:hover td {
  background-color: #f9fafb;
}

.file-table /deep/ .select-cell {
  width: 1%;
  padding-right: 0;
}

.file-table /deep/ tbody td:nth-child(5) {
  color: #6b7280;
  white-space: nowrap;
}

.file-name {
  display: flex;
  align-items: center;
  min-width: 0;
}

.file-icons {
  display: flex;
  flex: none;
  gap: 4px;
  margin-right: 12px;
}

.owner-note {
  color: #6b7280;
  font-size: 12px;
}

.file-icon {
  display: grid;
  flex: none;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background-color: #f3f4f6;
  color: #374151;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.04em;
}

.kind-pdf {
  background-color: #fdecec;
  color: #b42318;
}

.kind-doc {
  background-color: #e8f1ff;
  color: #0b55c9;
}

.kind-xls {
  background-color: #e6f6ec;
  color: #14703a;
}

.kind-img {
  background-color: #f1ebfb;
  color: #6b3fb0;
}

.file-name-text {
  min-width: 0;
}

.file-meta {
  color: #6b7280;
  font-size: 12px;
  line-height: 1.4;
}

.file-name-text b {
  font-weight: 700;
  overflow-wrap: anywhere;
}

.file-table /deep/ td .dropdown-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  min-height: 32px;
  padding: 0;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background-color: #ffffff;
  color: #374151;
}

.file-table /deep/ td .dropdown-toggle:hover,
.file-table /deep/ td .show > .dropdown-toggle {
  border-color: #006cff;
  background-color: #ffffff;
  color: #006cff;
}

.file-new {
  display: inline-block;
  white-space: nowrap;
  margin-left: 8px;
  padding: 1px 9px;
  background-color: #006cff;
  border-radius: 999px;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
}

.file-table /deep/ .folders-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.file-folder {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding: 1px 9px;
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  color: #374151;
  font-size: 11px;
  font-weight: 700;
}

.file-expiry {
  display: inline-block;
  white-space: nowrap;
  margin-left: 8px;
  padding: 1px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.expiry-expired {
  background-color: #fdecec;
  color: #b42318;
}

.expiry-expiring {
  background-color: #fff4e0;
  color: #9a5b00;
}

.file-folder-more {
  cursor: default;
}

.file-folder svg {
  margin-right: 4px;
  color: #6b7280;
}

/* Phones. Last in the file so these win over the base rules above.
   The type icon and the folder chips drop out (the type moves into the line
   under the name; the side panel lists the folders) and the table uses a fixed
   layout so the name gets everything left over. */
@media (max-width: 575.98px) {
  .file-table /deep/ .table {
    table-layout: fixed;
  }

  .file-table /deep/ thead th {
    padding: 8px 6px;
  }

  .file-table /deep/ tbody td {
    padding: 12px 6px;
    vertical-align: middle;
  }

  .file-table /deep/ th.select-cell,
  .file-table /deep/ td.select-cell {
    width: 38px;
    padding-right: 0;
    padding-left: 10px;
  }

  .file-table /deep/ thead th:last-child,
  .file-table /deep/ tbody td:last-child {
    width: 40px;
    padding-right: 6px;
    padding-left: 0;
  }

  .file-icons,
  .file-folder {
    display: none;
  }

  .file-name-text b {
    display: block;
    font-size: 15px;
    line-height: 1.3;
  }

  .file-new,
  .file-expiry {
    margin: 4px 6px 0 0;
    vertical-align: top;
  }

  .file-meta {
    margin-top: 2px;
  }

  .file-meta > span + span::before {
    content: '·';
    margin: 0 6px;
  }
}
</style>
